import React from 'react';
import { DeviceActionName, DeviceAttributeName } from 'dto/device-dto';
import { usePermissionContext } from './permission-provider';
import { notifyEditDeviceAttributePermissionDenied } from 'pages/private/components/notifications/permission-notification';

function getPermissionCodeByAttribute(name: DeviceAttributeName): string {
    switch (name) {
        case 'serialNumber':
            return 'Serial Number Modify';
        case 'esn':
            return 'eSN Modify';
        case 'location':
            return 'Location Modify';
        case 'name':
            return 'Name Modify';
        case 'timezone':
            return 'Timezone Modify';
        default:
            return name;
    }
}

function getPermissionCodeByAction(name: DeviceActionName): string {
    switch (name) {
        case DeviceActionName.RESTART:
            return 'Restart';
        case DeviceActionName.CHECK_HEALTH:
            return 'Check Health';
        case DeviceActionName.RESEND_GPG_KEYS:
            return 'Resend GPG keys';
        case DeviceActionName.RESEND_EXTERNAL_REPO_CONFIG:
            return 'Resend External Repo Config';
        default:
            return name;
    }
}
const useDevicePermissionContext = () => {
    const { hasPermission, runHandler } = usePermissionContext();
    const { hasAttributePermission, hasActionPermission } = React.useMemo(() => {
        return {
            hasAttributePermission: (name: DeviceAttributeName): boolean =>
                hasPermission(getPermissionCodeByAttribute(name)),
            hasActionPermission: (name: DeviceActionName): boolean => hasPermission(getPermissionCodeByAction(name)),
        };
    }, [hasPermission]);
    return {
        hasPermission,
        runHandler: (
                attributeName: DeviceAttributeName,
                action: () => Promise<unknown>,
                onMissingPermission?: () => unknown,
            ): Promise<unknown> => {
                const permissionCode = getPermissionCodeByAttribute(attributeName);
                return runHandler(
                    permissionCode,
                    action,
                    onMissingPermission ?? (() => notifyEditDeviceAttributePermissionDenied(permissionCode)),
                );
            },
        hasActionPermission,
        hasAttributePermission
    };
};
export { useDevicePermissionContext };